import type { ReactNode } from 'react'

import { CopyIcon } from '@/components/icons'
import { copy } from '@/lib/clipboard'
import { cn } from '@/lib/utils'

/** An on/off switch. */
export function Switch({
  checked,
  onChange,
  label,
  disabled,
  busy,
}: {
  checked: boolean
  onChange: (next: boolean) => void
  label: string
  disabled?: boolean
  busy?: boolean
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      title={label}
      disabled={disabled || busy}
      onClick={() => onChange(!checked)}
      className={cn(
        'relative inline-flex h-6 w-11 shrink-0 items-center rounded-pill transition-colors disabled:cursor-not-allowed disabled:opacity-50',
        checked ? 'bg-brand' : 'bg-line-strong',
        busy && 'animate-pulse',
      )}
    >
      <span className={cn('inline-block h-5 w-5 rounded-full bg-white shadow-card transition-transform', checked ? 'translate-x-[22px]' : 'translate-x-0.5')} />
    </button>
  )
}

/** A small icon button that copies `text`. */
export function CopyButton({ text, label = 'Copy', toastTitle, icon, className }: { text: string; label?: string; toastTitle?: string; icon?: ReactNode; className?: string }) {
  return (
    <button
      type="button"
      onClick={() => void copy(text, toastTitle ?? 'Copied')}
      aria-label={label}
      title={label}
      className={cn('tap grid h-7 w-7 shrink-0 place-items-center rounded-[7px] text-ink-muted transition-colors hover:bg-surface-muted hover:text-ink', className)}
    >
      {icon ?? <CopyIcon className="h-4 w-4" />}
    </button>
  )
}

/** A row of mutually exclusive choices (filters, ranges). */
export function Segmented<T extends string | number>({
  value,
  onChange,
  options,
  label,
  className,
}: {
  value: T
  onChange: (value: T) => void
  options: Array<{ value: T; label: string }>
  label: string
  className?: string
}) {
  return (
    <div role="radiogroup" aria-label={label} className={cn('inline-flex gap-1 rounded-field bg-surface-muted p-1', className)}>
      {options.map((option) => (
        <button
          key={String(option.value)}
          type="button"
          role="radio"
          aria-checked={value === option.value}
          onClick={() => onChange(option.value)}
          className={cn(
            'h-8 whitespace-nowrap rounded-[8px] px-3 text-[13px] font-medium transition-colors',
            value === option.value ? 'bg-surface text-ink shadow-card' : 'text-ink-muted hover:text-ink',
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  )
}

/** Previous / next for paginated tables. */
export function Pager({
  page,
  totalPages,
  totalItems,
  pageSize,
  onPage,
}: {
  page: number
  totalPages: number
  totalItems: number
  pageSize: number
  onPage: (page: number) => void
}) {
  if (totalItems === 0) return null
  const from = (page - 1) * pageSize + 1
  const to = Math.min(page * pageSize, totalItems)
  return (
    <div className="flex items-center justify-between gap-3 border-t border-line px-4 py-3 text-[13px] text-ink-muted sm:px-5">
      <span className="num">
        {from}–{to} of {totalItems}
      </span>
      <div className="flex items-center gap-1.5">
        <button type="button" disabled={page <= 1} onClick={() => onPage(page - 1)} className="h-8 rounded-[8px] border border-line px-3 font-medium text-ink transition-colors hover:bg-surface-muted disabled:cursor-not-allowed disabled:opacity-40">
          Previous
        </button>
        <button type="button" disabled={page >= totalPages} onClick={() => onPage(page + 1)} className="h-8 rounded-[8px] border border-line px-3 font-medium text-ink transition-colors hover:bg-surface-muted disabled:cursor-not-allowed disabled:opacity-40">
          Next
        </button>
      </div>
    </div>
  )
}

/** Table cell helpers: a header cell and a body cell with the portal's spacing. */
export const th = 'whitespace-nowrap px-4 py-2.5 text-left text-[11.5px] font-semibold uppercase tracking-[0.06em] text-ink-muted first:pl-5 last:pr-5'
export const td = 'whitespace-nowrap px-4 py-3 text-[13.5px] text-ink first:pl-5 last:pr-5'
